import React from 'react';
import { Form, Radio } from 'antd';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { ExportBuilderAdvancedTemplate } from '../../../../types/export_builder_advanced';
import FormInput from '../../global/Forms/FormInput';
import { AsyncDispatch } from '../../../../types/global';
import { updateExportBuilderAdvancedTemplate } from '../../../actions/brand/export_builder_advanced/update';
import DrawerFormik from '../../global/drawer/DrawerFormik';
import { fetchExportOptions } from '../../../actions/brand/export/fetch';
import { useAppDispatch } from '../../../store/ReduxStore';

export const createValidationSchemaTemplate = (t: (key: string) => string) =>
  Yup.object().shape({
    templateName: Yup.string().required('Required'),
    fileTypeId: Yup.number().required('Required'),
    csvSeparator: Yup.string().when(['fileTypeId'], ([fileTypeId], schema: any) =>
      fileTypeId === 1 ? schema.required('Required') : schema
    ),
    sheetName: Yup.string().when(['fileTypeId'], ([fileTypeId], schema: any) =>
      fileTypeId === 2
        ? schema
            .max(31, t('exportBuilderAdvanced:sheetNameTooLong'))
            .test(
              'invalid-chars',
              t('exportBuilderAdvanced:sheetNameInvalidChars'),
              (value: string | undefined) => !value || !/[/\\?*:[\]]/.test(value)
            )
            .test(
              'reserved-word',
              t('exportBuilderAdvanced:sheetNameReserved'),
              (value: string | undefined) => !value || value.toLowerCase() !== 'history'
            )
        : schema
    ),
  });

type ExportBuilderAddTemplateDrawerProps = {
  brandId?: number;
  visible: boolean;
  onClose: () => void;
  isNewTemplate: boolean;
  setNewTemplateState: (templateId: number) => void;
  selectedTemplate: ExportBuilderAdvancedTemplate | undefined;
  receiver: boolean;
};

const ExportBuilderAddTemplateDrawer: React.FC<ExportBuilderAddTemplateDrawerProps> = ({
  brandId,
  selectedTemplate,
  setNewTemplateState,
  isNewTemplate,
  onClose,
  visible,
  receiver,
}) => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();

  return (
    <DrawerFormik
      title={t('exportBuilderAdvanced:addTemplate')}
      width={480}
      visible={visible}
      initialValues={{
        templateName: selectedTemplate ? selectedTemplate.name : '',
        fileTypeId: selectedTemplate ? selectedTemplate.file_type_id : 2,
        csvSeparator: selectedTemplate ? selectedTemplate.csv_separator : '',
        sheetName: selectedTemplate ? selectedTemplate.sheet_name || '' : '',
      }}
      validationSchema={createValidationSchemaTemplate(t)}
      handleSaveButtonEnabled={({ dirty }) => dirty} // Enable save button for brand read only users
      onClose={onClose}
      onSubmit={(values, formikActions) => {
        const obj = {
          ...(selectedTemplate && { id: selectedTemplate.id }),
          csv_separator: values.csvSeparator,
          file_type_id: values.fileTypeId,
          name: values.templateName,
          parent_owner_brand_id: brandId,
          is_multi_sheet: false, // Always single sheet for new templates
          // Only include sheet_name if user provided a value, otherwise let backend use its default
          ...(values.sheetName &&
            values.sheetName.trim() && {
              sheet_name: values.sheetName.trim(),
            }),
        };

        dispatch(updateExportBuilderAdvancedTemplate(obj))
          .then(res => {
            if (isNewTemplate) {
              const id = res.value.data.id;
              setNewTemplateState(id);
              if (receiver) dispatch(fetchExportOptions('export'));
            }
            onClose();
          })
          .catch(() => {
            // Error handling could be added here
          })
          .finally(() => {
            formikActions.setSubmitting(false);
          });
      }}
      enableReinitialize
    >
      {({ setFieldValue, values }) => {
        return (
          <>
            <Form layout="vertical">
              <FormInput
                name="templateName"
                label={t('common:name')}
                className="w-full"
                testId="add-template-name"
              />
              <div className="flex items-center">
                <Form.Item label={t('exportBuilderAdvanced:exportType')}>
                  <div className="mr-4">
                    <Radio.Group
                      onChange={e => setFieldValue('fileTypeId', e.target.value)}
                      value={values.fileTypeId}
                    >
                      <Radio.Button value={2}>{t('glossary:excel')}</Radio.Button>
                      <Radio.Button value={1}>{t('glossary:csv')}</Radio.Button>
                    </Radio.Group>
                  </div>
                </Form.Item>
                {values.fileTypeId === 1 && (
                  <FormInput name="csvSeparator" label={t('exportBuilderAdvanced:csvDelimiter')} />
                )}
              </div>
              {values.fileTypeId === 2 && (
                <FormInput
                  name="sheetName"
                  label={t('exportBuilderAdvanced:sheetName')}
                  className="w-full"
                />
              )}
            </Form>
          </>
        );
      }}
    </DrawerFormik>
  );
};

export default ExportBuilderAddTemplateDrawer;
